import type { EventBus } from '../namespaces/event-bus'
import { eventEmitter } from '../namespaces/event-bus'
import {
  convertAppearanceToPresence,
  convertUsersAppearanceToPresenceSnapshots,
  createPresenceSubscription,
  getUsersAppearance,
  isCurrentUser,
  isPresenceMessage,
  hasSubscribers,
  isUserPresenceMessage,
} from './helpers'
import type {
  PresenceUpdated,
  UserPresenceUpdated,
  ShellServiceSubscriptionState,
  UserPresence,
  PresenceSnapshot,
} from './models'
import { getShellLogger } from '../../common/logger'
import type { EventEmitter } from '../namespaces/models'
import type { NotificationChannelEvents } from '../notification-channel'
import { NotificationChannelNamespace } from '../notification-channel'
import type { NotificationChannelEvent } from '../notification-channel/models'
import { getShellApiInstance } from '../../common/shell-api-helpers'
import { Presence } from '@getgo/chameleon-core'
import { onWindowUnload } from '../../common/helpers/window'
import { Timer, Interval } from '../notification-channel/helpers'
import type { ExternalUserKey } from '@ucc/common'
import type { PresenceService } from './service'

const UNSUBSCRIPTION_INTERVAL = 10000 // 10 seconds

export interface PresenceEventsEmitMap {
  /** {@link PresenceUpdated} */
  readonly presenceUpdate: EventEmitter<PresenceUpdated>
  /** {@link UserPresenceUpdated} */
  readonly userPresenceUpdate: EventEmitter<UserPresenceUpdated>
}

export const PresenceServiceNamespace = 'PresenceService'
export const NotificationChannelMessageTypePresence = 'presence'

export const PresenceEvents = {
  presenceUpdate: eventEmitter<PresenceUpdated>(),
  userPresenceUpdate: eventEmitter<UserPresenceUpdated>(),
} as const

export * from './models'
export * from './helpers'

type AccumulatedResolve = (value: PresenceSnapshot | PromiseLike<PresenceSnapshot>) => void

export class ShellPresenceService {
  private readonly presenceService: PresenceService
  private notificationChannelId?: string
  private readonly logger = getShellLogger()
  private isNotificationChannelAvailable(): boolean {
    return !!this.notificationChannelId
  }
  private oldNotificationChannelId?: string

  private externalUserKey: Readonly<string> = ''

  private emit: Readonly<PresenceEventsEmitMap>

  private unsubscribeInterval: Interval

  private accumulatedResolves: Map<string, AccumulatedResolve> = new Map()
  private accumulatorTimer: Timer | null = null

  readonly subscriptions = new Map<ExternalUserKey, ShellServiceSubscriptionState>()

  constructor(presenceService: PresenceService, notificationChannelId: string, eventBus: EventBus) {
    this.presenceService = presenceService
    this.notificationChannelId = notificationChannelId
    const { emit } = eventBus.register(PresenceServiceNamespace, PresenceEvents)
    const emitter = Object.create(null)
    emitter.presenceUpdate = emit.presenceUpdate as EventEmitter<PresenceUpdated>
    Object.freeze(emitter.presenceUpdate)
    emitter.userPresenceUpdate = emit.userPresenceUpdate as EventEmitter<UserPresenceUpdated>
    Object.freeze(emitter.userPresenceUpdate)
    this.emit = emitter as Readonly<PresenceEventsEmitMap>
    this.subscribeToChannelEvents(eventBus)
    this.unsubscribeInterval = new Interval(this.unsubscribeIntervalHandler, UNSUBSCRIPTION_INTERVAL)
  }

  /**
   * Get the current presence status for a list of users.
   *
   * Useful when you want to limit the amount of notification channel subscriptions and/or don't care to receive updates about a user's presence.
   */
  getPresenceSnapshots(externalUserKey: string): Promise<PresenceSnapshot> {
    return new Promise(resolve => {
      this.accumulatedResolves.set(externalUserKey, resolve)

      if (!this.accumulatorTimer) {
        this.accumulatorTimer = new Timer(() => {
          this.accumulatorTimer = null

          const userKeys = this.getAccumulatedUserKeys()
          const resolves = this.getAccumulatedResolvesCopy()
          this.resetAccumulatedUserKeys()

          this.fetchUserAppearance(userKeys, resolves)
        }, 250)
      }
    })
  }

  subscribeToChannelEvents(eventBus: EventBus) {
    const channelEvents = eventBus.subscribeTo<typeof NotificationChannelNamespace, typeof NotificationChannelEvents>(
      NotificationChannelNamespace,
    )

    channelEvents.connected.on(this.notificationChannelConnectedHandler)
    channelEvents.disconnected.on(this.notificationChannelDisconnectedHandler)
    channelEvents.message.on(this.handleNotificationEvent)

    onWindowUnload(() => {
      channelEvents.connected.removeListener(this.notificationChannelConnectedHandler)
      channelEvents.disconnected.removeListener(this.notificationChannelDisconnectedHandler)
      channelEvents.message.removeListener(this.handleNotificationEvent)
    })
  }

  notificationChannelConnectedHandler = () => {
    const { user, notificationChannel } = getShellApiInstance()

    // TODO change the channelId source : https://jira.ops.expertcity.com/browse/SCORE-1197
    this.notificationChannelId = notificationChannel?.id

    if (this.externalUserKey === '') {
      const externalUserKey = user.key ?? ''
      this.externalUserKey = externalUserKey
      Object.freeze(this.externalUserKey)
    }

    // If after a reconnection the notificationChannelId has changed, we invalidate old subscriptions.
    if (this.notificationChannelId !== this.oldNotificationChannelId) {
      this.unregisterSubscriptionsToPresenceService()
    }

    this.oldNotificationChannelId = this.notificationChannelId

    this.registerSubscriptionsToPresenceService()
    this.emitUserCurrentPresence()
  }

  notificationChannelDisconnectedHandler = () => {
    this.emit.presenceUpdate({ presence: Presence.DISCONNECTED, externalUserKey: this.externalUserKey })
    this.notificationChannelId = undefined
  }

  emitUserCurrentPresence() {
    const subscription = this.subscriptions.get(this.externalUserKey)
    // When the notification channel is recreated, the subscriptions are re created too. Thus, the presence
    // of the current user is undefined but the presence service will fetch it and emit the update
    if (subscription?.presence) {
      const { presence, userNote } = subscription
      this.emit.presenceUpdate({ presence, externalUserKey: this.externalUserKey, userNote })
    }
  }

  /**
   * Callback function will get called when there is already a subscription for a certain external user key
   */
  async subscribe(externalUserKey: string): Promise<void> {
    const subscription: ShellServiceSubscriptionState | undefined = this.subscriptions.get(externalUserKey)

    if (subscription) {
      const { userDoNotDisturb, userAppearance, userStatus, userNote, status, presence, appearance } = subscription
      this.increaseSubscriberCount(externalUserKey)
      this.emit.presenceUpdate({ appearance, presence, externalUserKey, userNote, status })
      if (isCurrentUser(externalUserKey)) {
        this.emit.userPresenceUpdate({
          externalUserKey,
          userDoNotDisturb,
          userAppearance,
          userStatus,
          userNote,
          appearance,
          status,
        })
      }
    } else {
      this.updateSubscription(externalUserKey, {
        subscriptionId: undefined,
        subscribersCount: 1,
      })

      let subscriptionId: string | undefined
      if (this.isNotificationChannelAvailable()) {
        subscriptionId = await this.createPresenceSubscriptionHandler(externalUserKey)
      }

      this.updateSubscription(externalUserKey, { subscriptionId })

      this.unsubscribeInterval.reset()
    }
  }

  createPresenceSubscriptionHandler(externalUserKey: string) {
    const presenceServiceSubscription = this.presenceService.createSubscription({
      externalUserKeys: [externalUserKey],
      // TODO handle undefined notificationChannelId https://jira.ops.expertcity.com/browse/SCAPI-459
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      channelId: this.notificationChannelId!,
    })

    this.unsubscribeInterval.reset()

    return createPresenceSubscription(externalUserKey, presenceServiceSubscription)
  }

  registerSubscriptionsToPresenceService() {
    this.subscriptions.forEach(async (value: ShellServiceSubscriptionState, externalUserKey: string) => {
      if (!value.subscriptionId) {
        const subscriptionId = await this.createPresenceSubscriptionHandler(externalUserKey)
        this.updateSubscription(externalUserKey, { subscriptionId, subscribersCount: value.subscribersCount })
      }
    })
  }

  unsubscribe(externalUserKey: string): void {
    const subscription = this.subscriptions.get(externalUserKey)

    if (!subscription) {
      return
    }

    this.decreaseSubscriberCount(externalUserKey)
    this.unsubscribeInterval.reset()
  }

  unregisterSubscriptionsToPresenceService() {
    this.subscriptions.forEach((value: ShellServiceSubscriptionState, externalUserKey: string) => {
      if (value.subscriptionId) {
        this.subscriptions.set(externalUserKey, {
          subscriptionId: undefined,
          subscribersCount: value.subscribersCount,
        })
      }
    })
  }

  /**
   * Get current user's presence.
   */
  async getUserPresence(): Promise<UserPresence> {
    try {
      const response = await this.presenceService.getUserPresence().toPromise()
      return response
    } catch (error) {
      this.logger.error(`Failed to get user presence with error: ${error}`)
      return await Promise.reject(error)
    }
  }

  /**
   * Change current user's presence.
   *
   * We can’t and shouldn’t be able to update someone else’s.
   *
   * Bookmarks:
   * - https://developer-internal.goto.com/apis/presence/v1#operation/setUserPresence
   */
  setUserPresence(presence: Partial<UserPresence>): Promise<UserPresence> {
    const response = this.presenceService.setUserPresence(presence).toPromise()
    response.catch(error => {
      this.logger.error(`Failed to set user presence with error: ${error}`)
    })
    return response
  }

  handleNotificationEvent = (message: NotificationChannelEvent<unknown>) => {
    if (isPresenceMessage(message)) {
      const messageContent = message.data.content
      const { appearance, externalUserKey, userNote, status } = messageContent
      if (isUserPresenceMessage(message)) {
        const { userDoNotDisturb, userAppearance, userStatus } = message.data.content
        this.updateSubscription(externalUserKey, { userDoNotDisturb, userAppearance, userNote, userStatus, status })
        this.emit.userPresenceUpdate({
          eventId: message.eventId,
          externalUserKey,
          userDoNotDisturb,
          userAppearance,
          userNote,
          userStatus,
          appearance,
          status,
        })
      }
      const presence = convertAppearanceToPresence(appearance)
      this.updateSubscription(externalUserKey, { presence, appearance, userNote, status })
      this.emit.presenceUpdate({
        eventId: message.eventId,
        externalUserKey,
        presence,
        appearance,
        userNote,
        status,
      })
    }
  }

  private increaseSubscriberCount(externalUserKey: string) {
    const previousValue = this.subscriptions.get(externalUserKey)
    if (previousValue) {
      this.subscriptions.set(externalUserKey, {
        ...previousValue,
        subscribersCount: ++previousValue.subscribersCount,
      })
    }
  }

  private getAccumulatedUserKeys() {
    return Array.from(this.accumulatedResolves.keys())
  }

  private getAccumulatedResolvesCopy() {
    return new Map(this.accumulatedResolves)
  }

  private resetAccumulatedUserKeys() {
    this.accumulatedResolves = new Map()
  }

  private fetchUserAppearance(userKeys: string[], resolves: Map<string, AccumulatedResolve>) {
    getUsersAppearance(userKeys).then(usersAppearances => {
      const presenceSnapshots = convertUsersAppearanceToPresenceSnapshots(usersAppearances)

      presenceSnapshots.forEach(presenceSnapshot => {
        const currentResolve = resolves.get(presenceSnapshot.externalUserKey)
        if (currentResolve) {
          currentResolve(presenceSnapshot)
        }
      })
    })
  }

  private decreaseSubscriberCount(externalUserKey: string) {
    const previousValue = this.subscriptions.get(externalUserKey)
    if (previousValue) {
      this.subscriptions.set(externalUserKey, {
        ...previousValue,
        subscribersCount: --previousValue.subscribersCount,
      })
    }
  }

  private updateSubscription = (externalUserKey: string, subscription: Partial<ShellServiceSubscriptionState>) => {
    this.subscriptions.set(externalUserKey, {
      ...this.subscriptions.get(externalUserKey),
      // TODO properly handle updating a subscription with no subscribers count https://jira.ops.expertcity.com/browse/SCAPI-460
      ...(subscription as ShellServiceSubscriptionState),
    })
  }

  private unsubscribeIntervalHandler = () => {
    Array.from(this.subscriptions.keys()).forEach(externalUserKey => {
      const subscription = this.subscriptions.get(externalUserKey)

      if (!hasSubscribers(subscription)) {
        if (subscription?.subscriptionId) {
          this.presenceService.deleteSubscriptionBySubscriptionId(subscription.subscriptionId)
        }

        this.subscriptions.delete(externalUserKey)
      }
    })
  }
}
